import React from 'react';
import { Shield, Award, Users, Sparkles } from 'lucide-react';

export const TrustBadges = () => {
  const badges = [
    {
      icon: Shield,
      title: 'Galpão Coberto',
      description: 'Veículo protegido'
    },
    {
      icon: Sparkles,
      title: 'Produtos Premium',
      description: 'Alta qualidade'
    },
    {
      icon: Users,
      title: 'Equipe Treinada',
      description: 'Profissionais capacitados'
    },
    {
      icon: Award,
      title: 'Resultados Garantidos',
      description: 'Satisfação total'
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {badges.map((badge, index) => (
        <div 
          key={index}
          className="flex flex-col items-center text-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="w-12 h-12 bg-[#00B4D8]/10 rounded-full flex items-center justify-center mb-2">
            <badge.icon className="w-6 h-6 text-[#00B4D8]" />
          </div>
          <h4 className="font-bold text-sm text-gray-900">{badge.title}</h4>
          <p className="text-xs text-gray-600">{badge.description}</p>
        </div>
      ))}
    </div>
  );
};

export const TrustSection = ({ neighborhood = '', serviceType = 'Lava Jato' }) => {
  const facadeImage = "https://customer-assets.emergentagent.com/job_preview-box7go/artifacts/bet992ui_Lava%20jato%20Estetica%20automotiva%20-%20Box7.png";
  
  return (
    <section className="relative py-20 overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${facadeImage})`,
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/80 to-[#00B4D8]/30"></div>
      
      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#00B4D8]/20 backdrop-blur-sm border border-[#00B4D8]/50 rounded-full px-4 py-2 mb-4">
              <span className="w-2 h-2 bg-[#00B4D8] rounded-full animate-pulse"></span>
              <span className="text-white text-sm font-semibold">CONFIANÇA E QUALIDADE</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              Por que milhares de clientes confiam na <span className="text-[#00B4D8]">Box7go</span>?
            </h2>
            <p className="text-xl text-gray-200">
              {neighborhood ? `Referência em ${serviceType.toLowerCase()} em ${neighborhood}` : 'Referência em estética automotiva e lava jato em Goiânia'}
            </p>
          </div>
          
          {/* Trust Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Shield, title: 'Galpão Coberto', description: 'Veículo 100% protegido' },
              { icon: Sparkles, title: 'Produtos Premium', description: 'Importados e profissionais' },
              { icon: Users, title: 'Equipe Especializada', description: 'Certificada e treinada' },
              { icon: Award, title: 'Resultados Garantidos', description: 'Satisfação total' }
            ].map((badge, index) => (
              <div 
                key={index}
                className="flex flex-col items-center text-center p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#00B4D8] rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-[#00B4D8]/30">
                  <badge.icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-bold text-lg text-white mb-1">{badge.title}</h4>
                <p className="text-sm text-gray-300">{badge.description}</p>
              </div>
            ))}
          </div>
          
          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 text-center hover:bg-white/20 transition-all duration-300">
              <div className="text-5xl font-extrabold text-[#00B4D8] mb-2">5+</div>
              <div className="text-white font-semibold text-lg">Anos de Experiência</div>
              <div className="text-gray-300 text-sm mt-1">Excelência comprovada</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 text-center hover:bg-white/20 transition-all duration-300">
              <div className="text-5xl font-extrabold text-[#00B4D8] mb-2">1000+</div>
              <div className="text-white font-semibold text-lg">Clientes Satisfeitos</div>
              <div className="text-gray-300 text-sm mt-1">Avaliação 5 estrelas</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 text-center hover:bg-white/20 transition-all duration-300">
              <div className="text-5xl font-extrabold text-[#00B4D8] mb-2">18</div>
              <div className="text-white font-semibold text-lg">Bairros Atendidos</div>
              <div className="text-gray-300 text-sm mt-1">Cobertura completa</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const BeforeAfterEmphasis = ({ title = 'Resultados que Impressionam' }) => {
  return (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 md:p-8 text-center">
      <div className="inline-flex items-center gap-4 mb-4">
        <div className="px-4 py-2 bg-red-500 text-white font-bold rounded">ANTES</div>
        <div className="text-2xl">→</div>
        <div className="px-4 py-2 bg-green-500 text-white font-bold rounded">DEPOIS</div>
      </div>
      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
        {title}
      </h3>
      <p className="text-gray-600">
        Transformação completa com produtos profissionais e técnicas avançadas
      </p>
    </div>
  );
};

export const ProfessionalEnvironment = () => {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-4">
        Ambiente Profissional de Primeira Linha
      </h3>
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-[#00B4D8] rounded-full flex items-center justify-center flex-shrink-0">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-1">Galpão Coberto e Protegido</h4>
            <p className="text-sm text-gray-600">
              Seu veículo totalmente protegido do sol e intempéries durante todo o serviço, garantindo melhor resultado e preservação da pintura.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-[#00B4D8] rounded-full flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-1">Produtos Premium Importados</h4>
            <p className="text-sm text-gray-600">
              Utilizamos apenas produtos automotivos de linha profissional, importados e de alta performance para garantir durabilidade.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-[#00B4D8] rounded-full flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-1">Equipe Extremamente Capacitada</h4>
            <p className="text-sm text-gray-600">
              Profissionais treinados regularmente nas melhores técnicas do mercado, com certificação em detalhamento automotivo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
